/**
 * Color Utilities
 */

/**
 * Convert a hex color to rgba string
 *
 * @param hex - Hex color string (#RRGGBB)
 * @param alpha - Alpha value (0–1)
 * @returns rgba string, or the original hex if malformed
 */
export function hexToRgba(hex: string, alpha: number): string {
  const result = /^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Returns a Tailwind text-color class for readable contrast on the given hex background.
 * Picks whichever of 'text-gray-900' / 'text-white' has the higher WCAG contrast ratio.
 *
 * @param hex - Hex color string (#RRGGBB)
 * @returns 'text-white' or 'text-gray-900'
 */
export function getContrastTextColor(hex: string): 'text-white' | 'text-gray-900' {
  return getContrastColor(hex) === '#000000' ? 'text-gray-900' : 'text-white';
}

/**
 * Calculate the readable foreground (black or white) for a given hex background.
 *
 * Uses sRGB-linearized luminance with a perceptual midpoint threshold of 0.5:
 *   - lum < 0.5  → white text   (saturated mid-tone & dark brand colours)
 *   - lum >= 0.5 → black text   (pastels, light tints, near-white)
 *
 * Why this vs WCAG contrast ratio: pure contrast-ratio comparison flips medium
 * mid-tone colours (e.g. green-400, orange-400) inconsistently because the formula
 * is asymmetric — black-on-light gets very high ratios, white-on-medium gets low
 * ratios, so a "max contrast" or "≥3:1 white" rule pushes black onto colours
 * where white is visually fine. A simple luminance threshold matches how designers
 * pick text colours: saturated brand colours stay white, light tints go dark.
 *
 * The threshold of 0.5 is calibrated so:
 *   - orange-400 (lum 0.41), green-500 (0.41), blue-500 (0.21), red-500 (0.20) → white
 *   - green-400 (0.55), green-300 (0.78), pastels (0.7+) → black
 *
 * @param hexColor - Hex color string (with or without #)
 * @returns '#ffffff' when the background is below the luminance midpoint, else '#000000'
 */
export function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) return '#000000';

  const toLinear = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };

  const r = toLinear(parseInt(hex.substring(0, 2), 16));
  const g = toLinear(parseInt(hex.substring(2, 4), 16));
  const b = toLinear(parseInt(hex.substring(4, 6), 16));
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return lum < 0.5 ? '#ffffff' : '#000000';
}
